import { Fragment, useState, useEffect, useRef } from "react";
import AddressesPreview from "../../../../utils/forms/address/AddressesPreview";
import AddressPopup from "../../popup/popups/AddressPopUp";
import { validateAddressCoverage } from "../../../../utils/validation/addressCoverageValidation";

const initialAddress = {
  address: "",
  suiteAddress: "",
  city: "",
  stateAddress: "Alabama",
  zipCode: "",
  country: "US",
  fromDate: "",
  toDate: "",
};

function MultiAddress({
  addresses,
  setFormState,
  description,
  fieldName = "addresses",
  taxYears = null, // Optional array of tax years to validate coverage
  formMethods = null, // Optional react-hook-form methods for validation
}) {
  const [addressesProp, setAddresses] = useState(addresses || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(initialAddress);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Use refs to store formMethods and taxYears to avoid dependency issues
  const formMethodsRef = useRef(formMethods);
  const taxYearsRef = useRef(taxYears);
  
  // Update refs when props change (without triggering effects)
  useEffect(() => {
    formMethodsRef.current = formMethods;
    taxYearsRef.current = taxYears;
  }, [formMethods, taxYears]);

  // Update local state when addresses prop changes (only if different)
  useEffect(() => {
    const addressesArray = addresses || [];
    // Only update if the arrays are actually different (deep comparison)
    setAddresses((prev) => {
      const prevStr = JSON.stringify(prev);
      const newStr = JSON.stringify(addressesArray);
      if (prevStr !== newStr) {
        return addressesArray;
      }
      return prev; // Return previous value to avoid unnecessary update
    });
  }, [addresses]); // Only depend on addresses prop

  // Sync addresses to formMethods whenever they change
  useEffect(() => {
    const fm = formMethodsRef.current;
    if (fm && addressesProp) {
      // Always sync addresses to formMethods so validator can access them
      fm.setValue(fieldName, addressesProp, {
        shouldValidate: false, // Don't validate on every change, only when needed
        shouldDirty: true,
      });
    }
  }, [addressesProp, fieldName]); // Only depend on addressesProp and fieldName

  // Validate address coverage when addresses or taxYears change
  useEffect(() => {
    const fm = formMethodsRef.current;
    const ty = taxYearsRef.current;
    if (ty && Array.isArray(ty) && ty.length > 0 && fm && addressesProp) {
      // Small delay to ensure setValue has completed
      const timeoutId = setTimeout(() => {
        // Trigger validation to run the custom validator
        fm.trigger(fieldName);
      }, 100);
      
      return () => clearTimeout(timeoutId); // Cleanup timeout
    }
  }, [addressesProp, fieldName]); // Only depend on addressesProp and fieldName

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentAddress(initialAddress);
    setEditingIndex(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (index) => {
    setCurrentAddress(addressesProp[index]);
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (index) => {
    const updated = addressesProp.filter((_, i) => i !== index);
    setAddresses(updated);
    setFormState((prev) => ({ ...prev, [fieldName]: updated }));
    
    // Update formMethods value and trigger validation
    const fm = formMethodsRef.current;
    if (fm) {
      fm.setValue(fieldName, updated, {
        shouldValidate: true,
        shouldDirty: true,
      });
      // Trigger validation to check coverage
      fm.trigger(fieldName);
    }
  };

  const handleSave = (address) => {
    const updated = [...addressesProp];
    if (editingIndex !== null) {
      updated[editingIndex] = address;
    } else {
      updated.push(address);
    }
    setAddresses(updated);
    setFormState((prev) => ({ ...prev, [fieldName]: updated }));
    
    // Update formMethods value and trigger validation
    const fm = formMethodsRef.current;
    if (fm) {
      fm.setValue(fieldName, updated, {
        shouldValidate: true,
        shouldDirty: true,
      });
      // Trigger validation to check coverage
      fm.trigger(fieldName);
    }
    
    setIsModalOpen(false);
  };

  return (
    <Fragment>
      <AddressesPreview
        addresses={addressesProp}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />
      <div>
        <button
          type="button"
          className="add-button"
          onClick={handleAddClick}
        >
          <span className="material-icons">+</span>
          <span>Add Address</span>
        </button>
      </div>

      {isModalOpen && (
        <AddressPopup
          editingIndex={editingIndex}
          setCurrentAddress={setCurrentAddress}
          currentAddress={currentAddress}
          setIsModalOpen={setIsModalOpen}
          handleSave={handleSave}
        />
      )}
    </Fragment>
  );
}

export default MultiAddress;
